import {
  Users,
  Scale,
  Lock,
  Unlock,
  ChevronLeft,
  Send,
  ArrowLeftRight,
  CheckCircle2,
  PenLine,
} from "lucide-react";
import type { ContractLifecycleState } from "@/components/LifecycleStateBadge";

export type LifecycleTransitionAction = {
  toState: ContractLifecycleState;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: "default" | "outline" | "destructive";
  primary?: boolean;
  requiresLegal?: boolean;
};

export const LIFECYCLE_TRANSITION_ACTIONS: Record<ContractLifecycleState, LifecycleTransitionAction[]> = {
  // No manual lifecycle actions in the draft state — the approval flow
  // (PrimaryWorkflowAction → RouteForApprovalDialog) is the canonical
  // entry point into internal review and drives the lifecycle from there.
  // Listing a duplicate "Submit for Internal Review" lifecycle action here
  // caused two identical CTAs to render in the contract header.
  draft: [],
  internal_review: [
    { toState: "draft", label: "Return to Draft", description: "Move back to drafting", icon: ChevronLeft, variant: "outline" },
    { toState: "legal_review", label: "Send to Legal", description: "Route to the legal team for review", icon: Scale, variant: "default", primary: true },
    { toState: "out_for_negotiation", label: "Send External", description: "Send to coach / representation", icon: Send, variant: "outline" },
  ],
  out_for_negotiation: [
    { toState: "counter_received", label: "Log Counter-Proposal", description: "Record a response from the external party", icon: ArrowLeftRight, variant: "default", primary: true },
    { toState: "legal_review", label: "Bring Back to Legal", description: "Move back to legal review without a counter", icon: Scale, variant: "outline" },
    { toState: "approved", label: "Accept & Approve", description: "External party accepted — move to approved", icon: CheckCircle2, variant: "default" },
  ],
  counter_received: [
    { toState: "internal_review", label: "Return to Internal Review", description: "Review the counter internally", icon: Users, variant: "default", primary: true },
    { toState: "legal_review", label: "Escalate to Legal", description: "Have legal assess the counter", icon: Scale, variant: "outline" },
    { toState: "out_for_negotiation", label: "Send Another Revision", description: "Send a revised version back externally", icon: Send, variant: "outline" },
  ],
  legal_review: [
    { toState: "internal_review", label: "Return to Internal Review", description: "Return to the athletic dept", icon: ChevronLeft, variant: "outline" },
    { toState: "locked", label: "Lock Document", description: "Legal locks all terms — no further edits allowed", icon: Lock, variant: "default", primary: true, requiresLegal: true },
    { toState: "out_for_negotiation", label: "Send External", description: "Send to coach / representation", icon: Send, variant: "outline" },
  ],
  locked: [
    { toState: "legal_review", label: "Unlock Document", description: "Return to legal review for further edits", icon: Unlock, variant: "outline", requiresLegal: true },
    { toState: "approved", label: "Approve Contract", description: "All terms agreed — mark as approved", icon: CheckCircle2, variant: "default", primary: true },
  ],
  approved: [
    { toState: "signed", label: "Mark as Signed", description: "Contract has been executed and signed", icon: PenLine, variant: "default", primary: true },
  ],
  signed: [],
};

/**
 * Compact "where am I in the workflow" stages used for the contract header
 * stepper. Maps every lifecycle state into one of four high-level stages so
 * the user always sees: Coach → Draft → Negotiate → Approve → Sign.
 */
export type LifecycleStage = "draft" | "negotiate" | "approve" | "signed";

export const LIFECYCLE_STAGE_FROM_STATE: Record<ContractLifecycleState, LifecycleStage> = {
  draft: "draft",
  internal_review: "draft",
  legal_review: "draft",
  out_for_negotiation: "negotiate",
  counter_received: "negotiate",
  locked: "approve",
  approved: "approve",
  signed: "signed",
};
